import useTranslation from 'next-translate/useTranslation'
import { useContext } from 'react'

import { ComparablesInfoBoxProps } from '../comparables-info'
import { FlexRow } from '@unreserved-frontend-v2/ui/flex/flex-row'
import { BarsIcon } from '@unreserved-frontend-v2/ui/icons/bars'
import { Button } from '@unreserved-frontend-v2/ui/button/button'
import { DEFAULT_LOCALE } from '@unreserved-frontend-v2/utils/locale'
import { currency } from '@unreserved-frontend-v2/utils/currency'
import { UserContext } from '../../../../../users/components/user-info-provider/user-context'
import { buildQueryStringFromFilters } from '../../../../utils/routing/build-query-string-from-filters'
import { DEFAULT_LISTING_DIRECTORY } from '../../../../utils/routing/constants'
import { PropertyListingStatus } from '@unreserved-frontend-v2/api/generated/graphql/types'
import { twMerge } from 'tailwind-merge'

export type MedianSoldPriceInfoProps = Pick<ComparablesInfoBoxProps, 'data' | 'place' | 'filterSelections'> & {
  isViewingSold: boolean
  displayedFilters: string
  placeTitle: string
}

export function MedianSoldPriceInfo({
  data,
  placeTitle,
  displayedFilters,
  filterSelections,
  isViewingSold,
}: MedianSoldPriceInfoProps) {
  const { t } = useTranslation()
  const { hasToken } = useContext(UserContext)

  if (!data?.soldListingsCount) {
    return null
  }

  const title = isViewingSold
    ? t(`listings:comparables-info.title-sold-homes`, {
        homes: t(`listings:comparables-info.number-home-sold`, {
          count: data?.soldListingsCount,
          number: data?.soldListingsCount?.toLocaleString(DEFAULT_LOCALE),
        }),
        place: placeTitle,
        havingFilters: displayedFilters
          ? t('listings:comparables-info.having-displayed-filters', {
              displayedFilters,
            })
          : '',
      })
    : t(`listings:comparables-info.sub-title-sold`, {
        homes: t(`listings:comparables-info.similar-homes-sold`, {
          count: data?.soldListingsCount,
          number: data?.soldListingsCount?.toLocaleString(DEFAULT_LOCALE),
        }),
      })

  return (
    <>
      <FlexRow className="justify-between pt-2.5 font-bold">
        {t('listings:comparables-info.median-sold-price')}
        <BarsIcon className="flex-shrink-0" />
      </FlexRow>
      <hr className="text-shades-200 mt-1.5 mb-2" />
      <p dangerouslySetInnerHTML={{ __html: title }} className="text-sm leading-5" />
      <div className="pt-1 text-base">
        <div
          className={twMerge('text-primary text-xl font-bold', !hasToken ? 'select-none blur-sm' : '')}
          data-nosnippet={!hasToken ? true : undefined}
          data-testid="sold-price-info"
        >
          {hasToken ? currency(Math.round(data?.salePrice)) : '$XXX,XXX'}
        </div>
      </div>
      {!isViewingSold ? (
        <Button
          href={buildQueryStringFromFilters(
            {
              ...filterSelections,
              listingStatuses: PropertyListingStatus.Sold,
            },
            `/${DEFAULT_LISTING_DIRECTORY}/[listing]`
          )}
          className="mt-2"
        >
          {t(`listings:comparables-info.for-sale-view-homes-cta`)}
        </Button>
      ) : null}
    </>
  )
}

export default MedianSoldPriceInfo
